'use client';

import type { ConvexReactClient } from 'convex/react';
import posthog from 'posthog-js';
import { api } from '@/convex/_generated/api';

/**
 * Tiny product-analytics client. `initTrack` is called once (from AnalyticsInit)
 * with the Convex client + a userId getter; then any code can `track(event)` —
 * no hook needed. Fire-and-forget: never throws, never blocks the UI.
 */
let _convex: ConvexReactClient | null = null;
let _getUserId: (() => string | undefined) | null = null;
let _anonId: string | undefined;

function anonId(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  if (_anonId) return _anonId;
  try {
    let id = localStorage.getItem('stx_anon');
    if (!id) {
      id = `anon-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem('stx_anon', id);
    }
    _anonId = id;
    return id;
  } catch {
    return undefined;
  }
}

export function initTrack(convex: ConvexReactClient, getUserId: () => string | undefined) {
  _convex = convex;
  _getUserId = getUserId;
}

/**
 * The PostHog half of the dual-write. Exported for tests.
 *
 * ENG-19 (#202): this used to read `window.posthog`, which the npm build of
 * posthog-js never assigns (only the `<script>` snippet does) — so for months
 * every client `track()` (cancel_flow_started, save_offer_shown,
 * sidebar_upgrade_clicked, …) reached Convex `analyticsEvents` but PostHog
 * received nothing. Use the module singleton PostHogProvider initialises.
 * `__loaded` is false until `posthog.init()` ran (and on the server), in
 * which case posthog-js would just log a warning and drop the event anyway.
 */
export function capturePostHog(
  event: string,
  props?: Record<string, unknown>,
  ph: { __loaded?: boolean; capture?: (...args: any[]) => unknown } = posthog,
): boolean {
  try {
    if (!ph?.__loaded || typeof ph.capture !== 'function') return false;
    ph.capture(event, props, { transport: 'sendBeacon' });
    return true;
  } catch {
    /* analytics never breaks the app */
    return false;
  }
}

export function track(event: string, props?: Record<string, unknown>) {
  // Dual-write to PostHog so funnels live in ONE query surface (the split
  // between Convex analyticsEvents and PostHog cost a whole misdiagnosis).
  // sendBeacon survives the navigation that follows exit-clicks (blog CTAs).
  capturePostHog(event, props);
  try {
    if (!_convex) return;
    const path = typeof location !== 'undefined' ? location.pathname : undefined;
    _convex
      .mutation(api.analytics.logEvent, {
        event,
        userId: _getUserId?.() || undefined,
        anonId: anonId(),
        path,
        props: props ? JSON.stringify(props) : undefined,
      })
      .catch(() => {});
  } catch {
    /* never let analytics break the app */
  }
}
